import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const CustomerAuthContext = createContext(null);

export const CustomerAuthProvider = ({ children }) => {
    const [customer, setCustomer] = useState(null);
    const [checked, setChecked] = useState(false);
    const [favorites, setFavorites] = useState(new Set());

    const loadFavorites = useCallback(async () => {
        try {
            const { data } = await api.get("/customer/favorites");
            setFavorites(new Set((data.favorites || []).map((f) => f.hotel_id)));
        } catch { setFavorites(new Set()); }
    }, []);

    const loadMe = useCallback(async () => {
        const token = localStorage.getItem("hs_customer_token");
        if (!token) { setChecked(true); return; }
        try {
            const { data } = await api.get("/customer/me");
            setCustomer(data);
            await loadFavorites();
        } catch {
            localStorage.removeItem("hs_customer_token");
            setCustomer(null);
            setFavorites(new Set());
        } finally {
            setChecked(true);
        }
    }, [loadFavorites]);

    useEffect(() => { loadMe(); }, [loadMe]);

    // Activity heartbeat: ping every 30s while page is visible
    useEffect(() => {
        if (!customer) return;
        const interval = setInterval(() => {
            if (document.visibilityState !== "visible") return;
            api.post("/customer/activity", { seconds: 30, page: window.location.pathname }).catch(() => {});
        }, 30000);
        return () => clearInterval(interval);
    }, [customer]);

    const login = async (email, password) => {
        const { data } = await api.post("/customer/login", { email, password });
        localStorage.setItem("hs_customer_token", data.access_token);
        setCustomer(data.user);
        await loadFavorites();
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/customer/register", payload);
        localStorage.setItem("hs_customer_token", data.access_token);
        setCustomer(data.user);
        return data.user;
    };

    const logout = () => {
        localStorage.removeItem("hs_customer_token");
        setCustomer(null);
        setFavorites(new Set());
    };

    const toggleFavorite = useCallback(async (hotelId) => {
        if (!customer) {
            return { ok: false, reason: "not_logged_in" };
        }
        const isFav = favorites.has(hotelId);
        // optimistic update
        const next = new Set(favorites);
        if (isFav) next.delete(hotelId); else next.add(hotelId);
        setFavorites(next);
        try {
            if (isFav) {
                await api.delete(`/customer/favorites/${hotelId}`);
            } else {
                await api.post(`/customer/favorites/${hotelId}`);
            }
            return { ok: true, added: !isFav };
        } catch (e) {
            // revert
            setFavorites(favorites);
            return { ok: false, reason: e.response?.data?.detail || "Hata" };
        }
    }, [customer, favorites]);

    return (
        <CustomerAuthContext.Provider value={{
            customer, isAuthed: !!customer, checked,
            login, register, logout, reload: loadMe,
            favorites, isFavorite: (id) => favorites.has(id), toggleFavorite, refreshFavorites: loadFavorites,
        }}>
            {children}
        </CustomerAuthContext.Provider>
    );
};

export const useCustomerAuth = () => useContext(CustomerAuthContext);
